/* eslint-disable react/react-in-jsx-scope */
import {
  Alert,
  AlertTitle,
  LinearProgress,
  Snackbar,
  Typography,
} from '@mui/material';
import type { GridPaginationModel, GridRowModesModel } from '@mui/x-data-grid';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import { useContext, useState } from 'react';
import { toast } from 'react-toastify';
import { v4 as uuid } from 'uuid';
import { read, utils } from 'xlsx';
import { useFetchUsers } from '../../../lib/query/userMapping';
import { AddComponentType } from '../../../constants/addComponentType';
import { ImportType } from '../../../constants/importType';
import TenantContext from '../../../lib/context/TenantContext';
import { useMsal } from '@azure/msal-react';
import { usePusher } from '../../../lib/hooks/usePusher';
import { uploadFileForImport } from '../../../lib/services/blob-service';
import { ValidateHeaders } from '../../../lib/services/importService';
import { getCurrentDate } from '../../../lib/utils/dateHelper';
import { Columns } from './grid-columns';
import { sendMessage } from '../../../lib/services/queue-service';
import FileUpload from '../../../components/fileUpload';

const customClasses = {
  root: 'table-striped table-bordered',
  columnHeaderTitle: 'fw-bold',
  columnHeader: 'header',
  cell: 'cell',
  toolbarContainer: 'table-toolbar px-5',
  overlay: '',
  pagination: 'pagination',
  tooltip: 'tooltip',
  tooltipArrow: 'arrow',
};

const UserMapping = () => {
  const { tenant } = useContext(TenantContext);
  const { accounts } = useMsal();
  const {
    data: user,
    refetch,
    isLoading,
  } = useFetchUsers({ tenantId: tenant.id });
  const [showErrorAlert, setShowErrorAlert] = useState<boolean>(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [showSuccessAlert, setShowSuccessAlert] = useState<boolean>(false);

  const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({});

  const [isProcessing, setIsProcessing] = useState<boolean>(false);

  const handleImportStarted = (data: any) => {
    toast.info(data.message);
    setIsProcessing(true);
  };

  const handleImportCompleted = (data: any) => {
    toast.success(data.message);
    setIsProcessing(false);
    refetch();
  };

  const handleImportFailed = (data: any) => {
    toast.error(data.message);
    setIsProcessing(false);
    refetch();
  };

  usePusher({
    channelName: `user-${accounts[0]?.idTokenClaims?.preferred_username}`,
    eventName: 'import-started',
    callback: handleImportStarted,
  });

  usePusher({
    channelName: `user-${accounts[0]?.idTokenClaims?.preferred_username}`,
    eventName: 'import-completed',
    callback: handleImportCompleted,
  });

  usePusher({
    channelName: `user-${accounts[0]?.idTokenClaims?.preferred_username}`,
    eventName: 'import-failed',
    callback: handleImportFailed,
  });

  const showError = () => {
    setShowErrorAlert(true);
  };

  const handleClose = () => {
    setShowErrorAlert(false);
  };

  const Headers = ['Email'];

  const onFileUpload = (file: File, reset: () => void) => {
    // Log the file name and type
    // console.log('File Name:', file.name);
    // console.log('File Type:', file.type);

    // Log the headers for file upload
    // console.log('Headers for File Upload:', Headers);

    // Read the file content
    const reader = new FileReader();
    reader.onload = event => {
      if (event.target && event.target.result) {
        const fileContent = event.target.result;

        // Parse Excel file content into JSON format
        const workbook = read(fileContent, { type: 'binary' });
        const sheetName = workbook.SheetNames?.[0]; // Ensure SheetNames is not undefined
        const worksheet = workbook.Sheets[sheetName || ''];

        if (!worksheet) {
          // console.error('Worksheet is undefined.');
          showError();
          return;
        }

        const jsonData = utils.sheet_to_json(worksheet, { header: 1 });
        // console.log('JSON Data:', jsonData);

        if (!jsonData?.length) {
          // console.error('Error: No data found in the Excel sheet.');
          showError();
          return;
        }

        // Validate headers and continue with the upload process
        ValidateHeaders(file, Headers).then(res => {
          if (res) {
            setShowSuccessAlert(true);
            const currentBatchId = uuid();
            const filePath = `import/${
              tenant.id
            }/user-mapping/${getCurrentDate()}/${currentBatchId}/${file?.name}`;
            setIsProcessing(true);
            uploadFileForImport(file, filePath)
              .then(() => {
                sendMessage(
                  'import-master',
                  JSON.stringify({
                    fileName: filePath,
                    tenantId: tenant.id,
                    importType: ImportType.User,
                    batchId: currentBatchId,
                    userId: accounts[0]?.idTokenClaims?.preferred_username,
                  }),
                );
              })
              .then(() => reset());
          } else {
            showError();
          }
        });
      } else {
        // console.error('Error reading file.');
      }
    };

    // Start reading the file as binary string
    reader.readAsBinaryString(file);
  };

  const onPaginationModelChange = (model: GridPaginationModel) => {
    setPage(model.page);
    setRowsPerPage(model.pageSize);
  };

  return (
    <div className="mt-4">
      <Typography variant="h4" sx={{ color: 'black', fontWeight: '600' }}>
        User Mapping
      </Typography>
      <div className="col-span-12 mb-2 grid justify-end">
        <FileUpload
          onFileUpload={onFileUpload}
          headers={Headers}
          addComponentType={AddComponentType.User}
        />
      </div>
      <div className="my-2">{isProcessing && <LinearProgress />}</div>

      <DataGrid
        rows={user ?? []}
        columns={Columns({ tenantId: tenant.id })}
        rowModesModel={rowModesModel}
        onRowModesModelChange={setRowModesModel}
        slots={{ toolbar: GridToolbar }}
        disableColumnSelector
        disableDensitySelector
        autoHeight
        slotProps={{
          toolbar: {
            showQuickFilter: true,
          },
        }}
        sx={{
          boxShadow: 2,
          borderColor: 'blueGrey',
          '& .MuiDataGrid-cell:hover': {
            color: 'primary.light',
          },
          '& .gridHeader--header': {
            color: '#000000',
            fontSize: 'large',
          },
          '& .MuiDataGrid-columnHeaderTitle': {
            fontWeight: '700',
            color: '#020617',
          },
          '.row-red': {
            background: '#FF0000',
            color: '#000000',
          },
        }}
        loading={isLoading}
        classes={customClasses}
        pageSizeOptions={[5, 10, 20]}
        paginationModel={{ page, pageSize: rowsPerPage }}
        onPaginationModelChange={onPaginationModelChange}
        getRowClassName={params =>
          params.row.IsSystemGenerated ? 'row-red' : ''
        }
      />
      <Snackbar
        open={showErrorAlert}
        autoHideDuration={3000}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert
          variant="filled"
          onClose={handleClose}
          severity="error"
          sx={{ width: '100%' }}>
          <AlertTitle>VALIDATION FAILED:</AlertTitle>
          FILE FORMAT DID NOT MATCH
        </Alert>
      </Snackbar>
      <Snackbar
        open={showSuccessAlert}
        autoHideDuration={1500}
        onClose={() => setShowSuccessAlert(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert
          variant="filled"
          onClose={() => setShowSuccessAlert(false)}
          severity="success"
          sx={{ width: '100%' }}>
          <AlertTitle>VALIDATION PASSED:</AlertTitle>
          UPLOADING FILE FOR PROCESSING.
        </Alert>
      </Snackbar>
    </div>
  );
};

export default UserMapping;
